import type { VideoStatus } from '@obras/shared';
import { CircleAlert, Loader2 } from 'lucide-react';

import { cn } from '../../lib/cn.js';
import { useVideoStatus } from './api.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * O quadro de um vídeo que ainda não pode ser tocado.
 *
 * Enquanto está aqui, este componente é quem **pergunta ao servidor** como anda
 * a transcodificação. A situação gravada na hora do envio é sempre
 * "processando", e o Bunny não avisa ninguém quando termina — sem esta pergunta,
 * o vídeo ficaria em processamento para sempre na tela, mesmo pronto do outro
 * lado. Foi exatamente o que aconteceu na primeira verificação no navegador.
 *
 * A pergunta sai daqui, e não da tela inteira, porque só existe enquanto existe
 * um vídeo esperando: quando o último fica pronto, o componente some e nada mais
 * é perguntado.
 *
 * O quadro **não é clicável de propósito**. Abrir o player de um vídeo que ainda
 * transcodifica entrega a tela de espera do próprio Bunny — fundo azul e
 * "Processing video", em inglês, dentro de um sistema em português. Quem espera
 * tem de ler o aviso daqui, não o deles.
 */
export function VideoEmEspera({
  mediaId,
  status,
  compacto = false,
}: {
  mediaId: string;
  status: VideoStatus;
  compacto?: boolean;
}) {
  const falhou = status === 'falhou';
  const consulta = useVideoStatus(mediaId, !falhou);
  const progresso = consulta.data?.progress ?? 0;

  /*
   * "Volte daqui a alguns minutos", e não um número fixo: o tempo acompanha a
   * duração do vídeo. Um clipe de dez segundos fica pronto em meio minuto, e
   * uma vistoria de vinte minutos leva bem mais — prometer cinco minutos
   * estaria errado nos dois casos. Quando o Bunny já informa o quanto andou, a
   * porcentagem responde melhor do que qualquer estimativa.
   */
  const aviso = falhou
    ? 'Este vídeo não pôde ser convertido e não está disponível.'
    : 'O vídeo ainda está sendo processado. Volte daqui a alguns minutos.';

  return (
    <div
      title={aviso}
      className={cn(
        'flex aspect-square flex-col items-center justify-center gap-1 bg-sunken text-center',
        compacto ? 'px-2' : 'px-3 gap-1.5',
      )}
    >
      {falhou ? (
        <CircleAlert {...ICON} size={compacto ? 16 : 18} className="text-danger" aria-hidden />
      ) : (
        <Loader2
          {...ICON}
          size={compacto ? 16 : 18}
          className="animate-spin text-ink-3"
          aria-hidden
        />
      )}

      <span
        className={cn(
          'leading-tight font-medium text-ink',
          compacto ? 'text-[11px]' : 'text-[12px]',
        )}
      >
        {falhou ? 'Vídeo indisponível' : 'Vídeo em processamento'}
      </span>

      {/* No quadro pequeno da linha do tempo a frase inteira não cabe — fica no
          `title`, e a porcentagem ocupa a linha que sobra. */}
      {!compacto ? (
        <span className="text-[11px] leading-tight text-ink-3">
          {falhou ? 'Não foi possível converter o arquivo' : 'Volte daqui a alguns minutos'}
        </span>
      ) : null}

      {!falhou && progresso > 0 ? (
        <span className="tabular text-[11px] text-ink-3">{progresso}% concluído</span>
      ) : null}
    </div>
  );
}
